import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import {
  createExternalRef,
  deleteExternalRef,
  listExternalRefs,
  type Artifact,
  type ArtifactExternalProvider,
  type ArtifactExternalRef,
  type ArtifactExternalSyncDirection,
  type CreateArtifactExternalRefBody,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { Button } from '@/components/ui/button';
import { ConfirmActionDialog } from './ConfirmActionDialog';

interface ArtifactExternalRefsPanelProps {
  artifact: Artifact;
}

export function ArtifactExternalRefsPanel({
  artifact,
}: ArtifactExternalRefsPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);

  const refsQuery = useQuery<ArtifactExternalRef[]>({
    queryKey: ['artifact', artifact.id, 'external-refs'],
    queryFn: () => listExternalRefs(artifact.id),
  });

  const [deleteTarget, setDeleteTarget] = useState<ArtifactExternalRef | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (refId: string) => deleteExternalRef(refId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'external-refs'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setDeleteTarget(null);
    },
    onError: err => {
      setDeleteError(err.message);
    },
  });

  const [provider, setProvider] = useState<ArtifactExternalProvider>('github');
  const [externalType, setExternalType] = useState('issue');
  const [externalId, setExternalId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [syncDirection, setSyncDirection] =
    useState<ArtifactExternalSyncDirection>('imported_from');
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: CreateArtifactExternalRefBody) => createExternalRef(artifact.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'external-refs'] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setExternalId('');
      setExternalUrl('');
      setCreateError(null);
    },
    onError: err => {
      setCreateError(err.message);
    },
  });

  function handleCreate(): void {
    setCreateError(null);
    if (!externalId.trim() || !externalUrl.trim()) {
      setCreateError('External ID and URL are required');
      return;
    }
    try {
      new URL(externalUrl.trim());
    } catch {
      setCreateError('External URL must be a valid URL');
      return;
    }
    createMutation.mutate({
      provider,
      externalType: externalType.trim(),
      externalId: externalId.trim(),
      externalUrl: externalUrl.trim(),
      syncDirection,
    });
  }

  if (refsQuery.isLoading) {
    return <div className="text-xs text-text-secondary">Loading external refs…</div>;
  }

  if (refsQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {refsQuery.error.message}
      </div>
    );
  }

  const refs = refsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-3 text-sm">
      {refs.length === 0 ? (
        <span className="text-xs text-text-secondary">No external references yet.</span>
      ) : (
        <ul className="flex flex-col gap-2">
          {refs.map(r => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded-md border border-border bg-surface/40 p-2"
            >
              <span className="rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                {r.provider}:{r.externalType}
              </span>
              <a
                href={r.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 items-center gap-1 text-primary hover:underline"
              >
                <span className="font-mono text-xs">{r.externalId}</span>
                <ExternalLink className="size-3" />
              </a>
              <span className="font-mono text-xs text-text-secondary">{r.syncDirection}</span>
              <Button
                size="sm"
                variant="outline"
                disabled={!isOwnerOrAdmin || deleteMutation.isPending}
                onClick={(): void => {
                  setDeleteError(null);
                  setDeleteTarget(r);
                }}
                className="inline-flex items-center gap-1.5"
              >
                <Trash2 className="size-4" />
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-2 border-t border-border pt-2">
        <span className="text-xs text-text-secondary">Add external reference</span>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={provider}
            onChange={e => {
              setProvider(e.target.value as ArtifactExternalProvider);
            }}
            disabled={!isOwnerOrAdmin}
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="Provider"
          >
            <option value="github">github</option>
          </select>
          <input
            type="text"
            value={externalType}
            onChange={e => {
              setExternalType(e.target.value);
            }}
            disabled={!isOwnerOrAdmin}
            placeholder="issue"
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="External type"
          />
          <input
            type="text"
            value={externalId}
            onChange={e => {
              setExternalId(e.target.value);
            }}
            disabled={!isOwnerOrAdmin}
            placeholder="42"
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="External ID"
          />
          <input
            type="url"
            value={externalUrl}
            onChange={e => {
              setExternalUrl(e.target.value);
            }}
            disabled={!isOwnerOrAdmin}
            placeholder="https://github.com/owner/repo/issues/42"
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="External URL"
          />
          <select
            value={syncDirection}
            onChange={e => {
              setSyncDirection(e.target.value as ArtifactExternalSyncDirection);
            }}
            disabled={!isOwnerOrAdmin}
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm disabled:opacity-60"
            aria-label="Sync direction"
          >
            <option value="none">none</option>
            <option value="imported_from">imported_from</option>
          </select>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!isOwnerOrAdmin || createMutation.isPending}
            className="inline-flex items-center justify-center gap-1.5"
          >
            <Plus className="size-4" />
            {createMutation.isPending ? 'Adding…' : 'Add reference'}
          </Button>
        </div>
        {createError && (
          <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
            {createError}
          </div>
        )}
      </div>
      <ConfirmActionDialog
        open={deleteTarget !== null}
        onOpenChange={next => {
          if (!next) setDeleteTarget(null);
        }}
        title="Remove external reference?"
        description={
          deleteTarget
            ? `This removes the link to ${deleteTarget.provider} ${deleteTarget.externalType} ${deleteTarget.externalId}. The remote system is not modified.`
            : ''
        }
        confirmText="Remove reference"
        confirmTone="danger"
        onConfirm={(): void => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        pending={deleteMutation.isPending}
        errorMessage={deleteError}
      />
    </div>
  );
}
